import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircle2, 
  XCircle, 
  Loader2, 
  ArrowRight,
  Phone,
  CreditCard,
  Shield,
  Tag,
  Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    let attempts = 0;
    const maxAttempts = 10;
    const pollInterval = 2000;

    const pollStatus = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/payments/status/${sessionId}`);
        const data = response.data;
        
        if (data.payment_status === 'paid') {
          setPaymentData(data);
          setStatus('success');
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, pollInterval);
        } else {
          setStatus('timeout');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, pollInterval);
        } else {
          setStatus('error');
        }
      }
    };

    pollStatus();
  }, [sessionId, BACKEND_URL]); // Added BACKEND_URL to dependencies

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-2xl p-8 text-center"
      >
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-sky-400 mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold text-white mb-2">Processing Payment</h1>
            <p className="text-slate-400">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Payment Successful!</h1>
            <p className="text-slate-400 mb-6">
              {paymentData?.metadata?.package_id === 'setup' 
                ? "Your setup is complete! Now choose your maintenance plan."
                : "Thank you for your purchase. We'll be in touch within 24 hours to get you set up."
              }
            </p>
            {paymentData && (
              <div className="bg-slate-800/50 rounded-xl p-4 mb-6 text-left">
                <p className="text-sm text-slate-400">Amount Paid</p>
                <p className="text-2xl font-bold text-white">
                  ${(paymentData.amount_total / 100).toFixed(2)} USD
                </p>
                {paymentData.metadata?.package_name && (
                  <p className="text-sm text-slate-500 mt-1">
                    {paymentData.metadata.package_name}
                  </p>
                )}
              </div>
            )}
            {paymentData?.metadata?.package_id === 'setup' ? (
              <Button 
                onClick={() => navigate(`/choose-plan?email=${encodeURIComponent(paymentData.customer_email || '')}&customer_id=${encodeURIComponent(paymentData.metadata?.customer_id || '')}`)}
                className="w-full bg-sky-500 hover:bg-sky-600 text-white"
              >
                Choose Your Plan <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            ) : (
              <Button 
                onClick={() => navigate('/client/login')}
                className="w-full bg-sky-500 hover:bg-sky-600 text-white"
              >
                Go to Dashboard
              </Button>
            )}
          </>
        )}

        {(status === 'error' || status === 'timeout') && (
          <>
            <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-amber-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Payment Processing</h1>
            <p className="text-slate-400 mb-6">
              Your payment is being processed. You'll receive a confirmation email shortly.
            </p>
            <Button 
              onClick={() => navigate('/')}
              className="w-full bg-sky-500 hover:bg-sky-600 text-white"
            >
              Return Home
            </Button>
          </>
        )}

        {status === 'expired' && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Session Expired</h1>
            <p className="text-slate-400 mb-6">
              Your payment session has expired. Please try again.
            </p>
            <Button 
              onClick={() => navigate('/')}
              className="w-full bg-sky-500 hover:bg-sky-600 text-white"
            >
              Return Home
            </Button>
          </>
        )}
      </motion.div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-2xl p-8 text-center"
      >
        <div className="w-20 h-20 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-6">
          <XCircle className="w-10 h-10 text-slate-400" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">Payment Cancelled</h1>
        <p className="text-slate-400 mb-6">
          No worries! Your payment was cancelled and you haven't been charged.
        </p>
        <div className="space-y-3">
          <Button 
            onClick={() => navigate('/')}
            className="w-full bg-sky-500 hover:bg-sky-600 text-white"
          >
            Return to Homepage
          </Button>
          <p className="text-sm text-slate-500">
            Questions? Contact us at sales@alaskaaiservices.com
          </p>
        </div>
      </motion.div>
    </div>
  );
}

export function PricingPage() {
  const [loading, setLoading] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoValid, setPromoValid] = useState(null);
  const [promoDiscount, setPromoDiscount] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');

  const validatePromo = async () => {
    if (!promoCode.trim()) return;
    
    setValidatingPromo(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/promo/validate/${promoCode}`);
      if (response.data.valid) {
        setPromoValid(true);
        setPromoDiscount(response.data.percent_off);
      } else {
        setPromoValid(false);
        setPromoDiscount(null);
      }
    } catch (error) {
      setPromoValid(false);
      setPromoDiscount(null);
    } finally {
      setValidatingPromo(false);
    }
  };

  const handleCheckout = async (packageId) => {
    // Adjust package ID for annual billing
    let actualPackageId = packageId;
    if (billingCycle === 'annual') {
      if (packageId === 'maintenance') {
        actualPackageId = 'maintenance_annual';
      }
    }
    
    setLoading(packageId);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/payments/checkout`, {
        package_id: actualPackageId,
        origin_url: window.location.origin,
        promo_code: promoValid ? promoCode : null
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('There was an error processing your request. Please try again.');
      setLoading(null);
    }
  };

  const calculatePrice = (originalPrice) => {
    if (promoValid && promoDiscount) {
      return originalPrice * (1 - promoDiscount / 100);
    }
    return originalPrice;
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => window.location.href = '/'}>
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center">
              <Phone className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-lg">Alaska AI Services</span>
          </div>
        </div>
      </header>

      {/* Pricing Section */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Simple, Transparent Pricing</h1>
          <p className="text-xl text-slate-400">
            Start capturing every lead today
          </p>
        </div>

        {/* Promo Code Section */}
        <div className="mb-8">
          <div className="max-w-md mx-auto bg-slate-900/50 border border-slate-800 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Tag className="w-5 h-5 text-sky-400" />
              <span className="font-medium">Have a promo code?</span>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Enter code"
                value={promoCode}
                onChange={(e) => {
                  setPromoCode(e.target.value.toUpperCase());
                  setPromoValid(null);
                  setPromoDiscount(null);
                }}
                className="bg-slate-800 border-slate-700 text-white uppercase"
                data-testid="promo-input"
              />
              <Button
                onClick={validatePromo}
                disabled={validatingPromo || !promoCode.trim()}
                variant="outline"
                className="border-slate-700 text-white hover:bg-slate-800"
                data-testid="apply-promo"
              >
                {validatingPromo ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Apply'
                )}
              </Button>
            </div>
            {promoValid === true && (
              <div className="mt-2 flex items-center gap-2 text-green-400 text-sm">
                <Check className="w-4 h-4" />
                {promoDiscount}% discount applied!
              </div>
            )}
            {promoValid === false && (
              <div className="mt-2 text-red-400 text-sm">
                Invalid promo code
              </div>
            )}
          </div>
        </div>

        {/* Billing Cycle Toggle */}
        <div className="flex justify-center mb-8">
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-1 inline-flex">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                billingCycle === 'monthly' 
                  ? 'bg-sky-500 text-white' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                billingCycle === 'annual' 
                  ? 'bg-sky-500 text-white' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Annual
              <span className="ml-2 text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                2 months free
              </span>
            </button>
          </div>
        </div>

        <div className="max-w-lg mx-auto">
          {/* Setup Package - Only public option */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-slate-900 border-2 border-sky-500 rounded-2xl p-8 relative"
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-sky-500 rounded-full text-sm font-semibold">
              Get Started
            </div>
            <h2 className="text-2xl font-bold mb-2 text-center">AI Setup Package</h2>
            <p className="text-slate-400 text-sm mb-6 text-center">One-time setup fee • Everything you need</p>
            <div className="mb-6 text-center">
              {promoValid && promoDiscount ? (
                <>
                  <span className="text-lg text-slate-500 line-through mr-2">$1,997</span>
                  <span className="text-5xl font-bold text-green-400">${calculatePrice(1997).toLocaleString()}</span>
                </>
              ) : (
                <span className="text-5xl font-bold">$1,997</span>
              )}
              <span className="text-slate-400 ml-2">one-time</span>
            </div>
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-3 text-slate-300">
                <CheckCircle2 className="w-5 h-5 text-sky-400 flex-shrink-0" />
                Custom AI trained for your business
              </li>
              <li className="flex items-center gap-3 text-slate-300">
                <CheckCircle2 className="w-5 h-5 text-sky-400 flex-shrink-0" />
                AI Phone Receptionist (calls & texts)
              </li>
              <li className="flex items-center gap-3 text-slate-300">
                <CheckCircle2 className="w-5 h-5 text-sky-400 flex-shrink-0" />
                WhatsApp & SMS automation
              </li>
              <li className="flex items-center gap-3 text-slate-300">
                <CheckCircle2 className="w-5 h-5 text-sky-400 flex-shrink-0" />
                Calendar booking integration
              </li>
              <li className="flex items-center gap-3 text-slate-300">
                <CheckCircle2 className="w-5 h-5 text-sky-400 flex-shrink-0" />
                Client dashboard access
              </li>
              <li className="flex items-center gap-3 text-slate-300">
                <CheckCircle2 className="w-5 h-5 text-sky-400 flex-shrink-0" />
                48-hour setup guarantee
              </li>
              <li className="flex items-center gap-3 text-slate-300">
                <CheckCircle2 className="w-5 h-5 text-sky-400 flex-shrink-0" />
                30-day money-back guarantee
              </li>
            </ul>
            <Button
              onClick={() => handleCheckout('setup')}
              disabled={loading === 'setup'}
              className="w-full bg-sky-500 hover:bg-sky-600 text-white py-5 font-semibold text-lg"
              data-testid="checkout-setup"
            >
              {loading === 'setup' ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Get Started <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </Button>
            
            {/* Maintenance info note */}
            <div className="mt-6 pt-6 border-t border-slate-700">
              <p className="text-slate-400 text-sm text-center">
                After purchase, choose your ongoing maintenance plan:
              </p>
              <div className="flex justify-center gap-4 mt-3 text-xs text-slate-500">
                <span className="px-3 py-1 bg-slate-800 rounded-full">Standard: $47.98/mo</span>
                <span className="px-3 py-1 bg-slate-800 rounded-full border border-yellow-500/30 text-yellow-400">Premium: $397/mo</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Trust Badges */}
        <div className="mt-12 flex flex-wrap justify-center gap-8 text-slate-400">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-green-400" />
            <span>100% Money-Back Guarantee</span>
          </div>
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-sky-400" />
            <span>Secure Stripe Payments</span>
          </div>
        </div>
      </main>
    </div>
  );
}

export default PricingPage;
